import React from "react";
import PropTypes from "prop-types";
import { ButtonBack, EmptyState } from "../../components/ui";
import ConversationItem from "./ConversationItem";
import QueueTabs from "./QueueTabs";
import TagFilter from "./TagFilter";
import { CHANNEL_COLORS } from "./messageCenterHelpers";
import styles from "./ConversationList.module.css";

const FILTERS = [
  { value: "all", label: "Todos" },
  ...Object.entries(CHANNEL_COLORS).map(([value, cfg]) => ({
    value,
    label: cfg.label,
  })),
];

/** Estado vazio por fila (sem busca ativa). Textos definidos na F2.2. */
const EMPTY_BY_QUEUE = {
  INBOX: { icon: "🎉", text: "Nenhuma conversa nova. Bom trabalho!" },
  WAITING: { icon: "⏳", text: "Nada esperando por você" },
  DONE: { icon: "✅", text: "Nada finalizado ainda" },
};

/** Skeleton de um item da lista (estado de loading). */
function ItemSkeleton() {
  return (
    <div className={styles.skeletonItem} aria-hidden="true">
      <span className={styles.skeletonAvatar} />
      <span className={styles.skeletonLines}>
        <span className={styles.skeletonLineShort} />
        <span className={styles.skeletonLineLong} />
      </span>
    </div>
  );
}

/**
 * Painel esquerdo: cabeçalho com voltar/título, busca local, chips de filtro por
 * canal e a lista de conversas. Estados de loading, vazio, erro e busca sem
 * resultado.
 */
export default function ConversationList({
  sessions,
  loading,
  error,
  search,
  onSearch,
  filter,
  onFilter,
  queue,
  queueCounts,
  onQueue,
  availableTags,
  tagFilter,
  onTagFilter,
  selectedId,
  onSelect,
  onRetry,
  crmEnabled = true,
}) {
  const renderList = () => {
    if (loading && sessions.length === 0) {
      return (
        <div className={styles.list}>
          {Array.from({ length: 5 }).map((_, i) => (
            <ItemSkeleton key={i} />
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <EmptyState
          fill
          role="alert"
          icon="⚠️"
          title="Não foi possível carregar"
          description="Ocorreu um erro ao buscar as conversas."
          action={
            <button type="button" className={styles.retryBtn} onClick={onRetry}>
              Tentar novamente
            </button>
          }
        />
      );
    }

    if (sessions.length === 0 && search.trim()) {
      return (
        <EmptyState
          fill
          icon="🔍"
          description={`Nenhuma conversa encontrada para “${search.trim()}”`}
        />
      );
    }

    if (sessions.length === 0) {
      // Básico (sem filas): estado vazio genérico, já que não há aba de fila.
      const emptyState = crmEnabled
        ? (EMPTY_BY_QUEUE[queue] ?? EMPTY_BY_QUEUE.INBOX)
        : { icon: "💬", text: "Nenhuma conversa ainda." };
      return (
        <EmptyState fill icon={emptyState.icon} description={emptyState.text} />
      );
    }

    return (
      <div className={styles.list}>
        {sessions.map((s) => (
          <ConversationItem
            key={s.id}
            session={s}
            active={s.id === selectedId}
            onSelect={onSelect}
            crmEnabled={crmEnabled}
          />
        ))}
      </div>
    );
  };

  return (
    <aside className={styles.panel}>
      <div className={styles.header}>
        <ButtonBack to="/agenda" size="small" />
        <h1 className={styles.title}>Centro de Mensagens</h1>

        <div className={styles.searchWrap}>
          <span className={styles.searchIcon} aria-hidden="true">
            🔍
          </span>
          <input
            type="search"
            className={styles.search}
            placeholder="Buscar por nome, telefone compartilhado ou @..."
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            aria-label="Buscar conversas"
          />
        </div>

        {/* Abas de fila (Entrada/Esperando/Finalizados) são recurso de equipe
            (CRM) — só o Profissional as vê. O Básico vê a lista completa. */}
        {crmEnabled && (
          <QueueTabs active={queue} counts={queueCounts} onChange={onQueue} />
        )}

        <div className={styles.filters} role="tablist" aria-label="Filtrar por canal">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={filter === f.value}
              className={`${styles.chip} ${filter === f.value ? styles.chipActive : ""}`}
              onClick={() => onFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Filtro por tag também é recurso de equipe (CRM). */}
        {crmEnabled && (
          <TagFilter
            tags={availableTags}
            value={tagFilter}
            onChange={onTagFilter}
          />
        )}
      </div>

      {renderList()}
    </aside>
  );
}

ConversationList.propTypes = {
  sessions: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  filter: PropTypes.string.isRequired,
  onFilter: PropTypes.func.isRequired,
  queue: PropTypes.oneOf(["INBOX", "WAITING", "DONE"]).isRequired,
  queueCounts: PropTypes.shape({
    INBOX: PropTypes.number,
    WAITING: PropTypes.number,
    DONE: PropTypes.number,
  }),
  onQueue: PropTypes.func.isRequired,
  availableTags: PropTypes.arrayOf(PropTypes.string),
  tagFilter: PropTypes.string,
  onTagFilter: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  crmEnabled: PropTypes.bool,
};
